// src/components/events/EventCard.jsx
import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  MapPin,
  Users,
  Route,
  IndianRupee,
  Clock,
  Building2,
} from "lucide-react";

/* ── helpers ─────────────────────────────────────────── */

const EVENT_TYPE_STYLES = {
  cycle_tour: {
    label: "Cycle Tour",
    bg: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  },
  cycle_kumbh: {
    label: "Cycle Kumbh",
    bg: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  },
  flash_mob: {
    label: "Flash Mob",
    bg: "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300",
  },
  koset_ride: {
    label: "Koset Ride",
    bg: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return { day: "", month: "" };
  const d = new Date(dateStr);
  return {
    day: d.getDate(),
    month: d.toLocaleDateString("en-IN", { month: "short" }).toUpperCase(),
  };
};

/* ── component ───────────────────────────────────────── */

const EventCard = ({ event, index = 0 }) => {
  const navigate = useNavigate();
  const typeConfig = EVENT_TYPE_STYLES[event.event_type] || {
    label: event.event_type,
    bg: "bg-gray-100 text-gray-700",
  };
  const startDate = formatDateShort(event.start_date);
  const locationStr = [event.location?.city, event.location?.state_code]
    .filter(Boolean)
    .join(", ");

  return (
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      whileHover={{ y: -6 }}
      className="h-full cursor-pointer group"
      onClick={() => navigate(`/events/${event._id}`)}
    >
      <div className="relative h-full bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-200/70 dark:border-gray-800 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col">
        {/* ── Image / Top section ── */}
        <div className="relative h-44 overflow-hidden bg-gradient-to-br from-green-500 to-teal-600 dark:from-green-700 dark:to-teal-800 shrink-0">
          {/* Pattern overlay */}
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" viewBox="0 0 200 200">
              <defs>
                <pattern id={`dots-${event._id}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                  <circle cx="2" cy="2" r="1.5" fill="white" />
                </pattern>
              </defs>
              <rect width="200" height="200" fill={`url(#dots-${event._id})`} />
            </svg>
          </div>

          {/* Cycling icon watermark */}
          <div className="absolute inset-0 flex items-center justify-center opacity-10">
            <Route className="w-24 h-24 text-white" />
          </div>

          {/* Date ribbon */}
          <div className="absolute top-4 left-4 bg-white dark:bg-gray-900 rounded-xl px-3 py-2 text-center shadow-lg min-w-[56px]">
            <span className="block text-xs font-bold text-green-600 dark:text-green-400 leading-tight">
              {startDate.month}
            </span>
            <span className="block text-xl font-extrabold text-gray-900 dark:text-white leading-tight">
              {startDate.day}
            </span>
          </div>

          {/* Event type badge */}
          <div className="absolute top-4 right-4">
            <span
              className={`inline-block px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm ${typeConfig.bg}`}
            >
              {typeConfig.label}
            </span>
          </div>

          {/* Bottom gradient for readability */}
          <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-black/30 to-transparent" />

          {/* Price badge */}
          <div className="absolute bottom-3 right-4">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-white/90 dark:bg-gray-900/90 text-gray-900 dark:text-white backdrop-blur-sm shadow">
              {event.is_paid ? (
                <>
                  <IndianRupee className="w-3 h-3" />
                  {event.entry_fee}
                </>
              ) : (
                <span className="text-green-600 dark:text-green-400">FREE</span>
              )}
            </span>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="p-5 flex flex-col grow">
          {/* Title */}
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors">
            {event.title}
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 line-clamp-2 leading-relaxed">
            {event.description}
          </p>

          {/* Meta info */}
          <div className="space-y-2 mb-4 grow">
            {locationStr && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <MapPin className="w-4 h-4 text-green-500 shrink-0" />
                <span className="truncate">{locationStr}</span>
              </div>
            )}

            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Calendar className="w-4 h-4 text-orange-500 shrink-0" />
              <span>
                {formatDate(event.start_date)}
                {event.end_date &&
                  event.end_date !== event.start_date &&
                  ` — ${formatDate(event.end_date)}`}
              </span>
            </div>

            {event.event_route?.distance > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Route className="w-4 h-4 text-blue-500 shrink-0" />
                <span>{event.event_route.distance.toFixed(1)} km</span>
              </div>
            )}
          </div>

          {/* Footer bar */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800 mt-auto">
            {/* Organizer */}
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 min-w-0">
              <Building2 className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">
                {event.organizer_id?.organization_name || "Organizer"}
              </span>
            </div>

            {/* Participants */}
            <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 shrink-0">
              <Users className="w-3.5 h-3.5" />
              <span>
                {event.registration_count ?? 0}
                {event.capacity === 0 ? " / Unlimited" : (event.capacity > 0 ? ` / ${event.capacity}` : "")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default EventCard;
